export interface Rng {
  next(): number;
  range(min: number, max: number): number;
  int(min: number, maxExclusive: number): number;
  chance(probability: number): boolean;
  pick<T>(items: readonly T[]): T;
  weighted<T>(items: readonly T[], weights: readonly number[]): T;
  sign(): number;
  fork(salt: number): Rng;
}

export function createRng(seed: number): Rng {
  let state = seed >>> 0;

  const next = (): number => {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };

  const range = (min: number, max: number): number => min + next() * (max - min);

  const int = (min: number, maxExclusive: number): number => Math.floor(range(min, maxExclusive));

  const chance = (probability: number): boolean => next() < probability;

  const pick = <T,>(items: readonly T[]): T => {
    if (items.length === 0) throw new Error("pick() called with an empty list");
    return items[int(0, items.length)] as T;
  };

  const weighted = <T,>(items: readonly T[], weights: readonly number[]): T => {
    if (items.length !== weights.length) throw new Error("weighted() length mismatch");
    let total = 0;
    for (const weight of weights) total += weight;
    let roll = next() * total;
    for (let i = 0; i < items.length; i++) {
      roll -= weights[i] as number;
      if (roll <= 0) return items[i] as T;
    }
    return items[items.length - 1] as T;
  };

  const sign = (): number => (next() < 0.5 ? -1 : 1);

  const fork = (salt: number): Rng => createRng((state ^ Math.imul(salt, 0x9e3779b9)) >>> 0);

  return { next, range, int, chance, pick, weighted, sign, fork };
}
